import { useTheme } from '@mui/material';
import { useColorScheme } from 'hooks/useColorScheme';

type TextLogoProps = {
  className?: string;
  /**
   * Fill color for the logo SVG paths.
   * Defaults to `theme.palette.text.primary` in light mode and
   * `theme.palette.primary.main` in dark mode.
   */
  color?: string;
};

export const TextLogo = (props: TextLogoProps): JSX.Element => {
  const theme = useTheme();
  const { isDarkMode } = useColorScheme();
  const defaultColor = isDarkMode
    ? theme.palette.primary.main
    : theme.palette.text.primary;
  const { className, color = defaultColor } = props;

  return (
    <svg
      width="168"
      height="20"
      className={className}
      viewBox="0 0 168 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="WatchDuty"
    >
      <g clipPath="url(#watchduty-text-logo-clip)">
        <path
          d="M21.9752 0.266235L18.4037 13.323H18.2425L15.288 0.266235H11.1759L7.74924 13.295H7.588L4.9589 0.266235H0L5.63476 19.7424H9.57697L12.9891 7.6079H13.1417L16.3452 19.7424H20.6949L26.9476 0.266235H21.9752Z"
          fill={color}
        />
        <path
          d="M30.4718 0.266235L23.9072 19.7424H28.7425L30.0643 15.5294H36.653L37.9092 19.7424H42.7445L36.0505 0.266235H30.4727H30.4718ZM31.1476 12.0775L33.3654 5.01179H33.518L35.6247 12.0775H31.1486H31.1476Z"
          fill={color}
        />
        <path
          d="M55.2773 0.266235H39.2275V4.0895H45.1664V19.7424H49.6117V4.0895H55.2773V0.266235Z"
          fill={color}
        />
        <path
          d="M86.7583 8.15973H78.8401V0.266235H74.3379V19.7424H78.8401V11.8402H86.7583V19.7424H91.2605V0.266235H86.7583V8.15973Z"
          fill={color}
        />
        <path
          d="M113.295 1.43164C111.864 0.655025 110.161 0.266235 108.188 0.266235H100.972V19.7424H108.169C110.148 19.7424 111.855 19.3526 113.289 18.5722C114.723 17.7926 115.827 16.6745 116.601 15.2197C117.375 13.7649 117.762 12.0225 117.762 9.9937C117.762 7.96485 117.375 6.22446 116.601 4.77253C115.826 3.3206 114.724 2.20633 113.293 1.42971L113.295 1.43164ZM112.656 13.4233C112.254 14.2887 111.66 14.9177 110.877 15.3113C110.093 15.705 109.13 15.9008 107.988 15.9008H105.474V4.10783H107.95C109.105 4.10783 110.076 4.30463 110.862 4.69728C111.649 5.09089 112.246 5.71797 112.652 6.58045C113.057 7.44293 113.261 8.58035 113.261 9.99466C113.261 11.409 113.059 12.558 112.656 13.4233Z"
          fill={color}
        />
        <path
          d="M131.039 12.4673C131.039 13.1648 130.885 13.7861 130.577 14.3312C130.269 14.8762 129.84 15.3027 129.287 15.6104C128.735 15.9182 128.091 16.0716 127.355 16.0716C126.62 16.0716 125.974 15.9182 125.419 15.6104C124.863 15.3027 124.432 14.8762 124.124 14.3312C123.816 13.7861 123.662 13.1648 123.662 12.4673V0.266235H119.16V12.857C119.16 14.2964 119.501 15.5516 120.184 16.6234C120.865 17.6952 121.82 18.5249 123.048 19.1153C124.276 19.7048 125.711 20 127.355 20C129 20 130.415 19.7048 131.643 19.1153C132.871 18.5258 133.827 17.6952 134.513 16.6234C135.198 15.5516 135.541 14.2964 135.541 12.857V0.266235H131.039V12.4673Z"
          fill={color}
        />
        <path
          d="M162.997 0.266235L158.961 8.49256H158.789L154.554 0.266235H153.15H149.523H136.712V4.0895H142.568V19.7424H147.012V4.0895H151.643L156.638 13.105V19.7424H161.112V13.105L168 0.266235H162.997Z"
          fill={color}
        />
        <path
          d="M67.9107 14.2646C67.6857 14.6447 67.4028 14.9679 67.0639 15.2342C66.7241 15.5005 66.333 15.7031 65.8889 15.8429C65.4448 15.9828 64.9562 16.0523 64.4232 16.0523C63.478 16.0523 62.6544 15.8256 61.9534 15.3721C61.2525 14.9187 60.7079 14.2424 60.3208 13.3414C59.9336 12.4413 59.7405 11.3289 59.7405 10.0034C59.7405 8.67783 59.9307 7.63205 60.3111 6.73195C60.6915 5.83185 61.2341 5.14399 61.939 4.66837C62.6438 4.19275 63.4809 3.95543 64.4512 3.95543C64.9968 3.95543 65.4969 4.03164 65.9507 4.18407C66.4045 4.3365 66.7994 4.55453 67.1354 4.84009C67.4714 5.12566 67.7456 5.4691 67.959 5.87236C68.1714 6.27466 68.3095 6.73002 68.3732 7.23747L72.8503 6.64512C72.6997 5.73055 72.4303 4.9028 72.0373 4.16574C71.5613 3.27143 70.9443 2.51604 70.1864 1.89764C69.4285 1.27924 68.5557 0.808451 67.5689 0.485264C66.5821 0.162076 65.5046 0 64.3373 0C62.611 0 61.0555 0.38879 59.669 1.1654C58.2826 1.94202 57.1828 3.07559 56.3708 4.56514C55.5588 6.0547 55.1523 7.86841 55.1523 10.0053C55.1523 12.1422 55.5521 13.9453 56.3515 15.4358C57.151 16.9254 58.2439 18.0609 59.6304 18.8404C61.0169 19.6199 62.5859 20.0096 64.3373 20.0096C65.6446 20.0096 66.809 19.8119 67.8306 19.4154C68.8521 19.0189 69.7249 18.4854 70.4481 17.8129C71.1712 17.1415 71.7428 16.3861 72.1609 15.5497C72.4959 14.8801 72.7257 14.2 72.8532 13.5102L68.3722 12.9622C68.2892 13.4504 68.1357 13.8845 67.9107 14.2646Z"
          fill={color}
        />
      </g>
      <defs>
        <clipPath id="watchduty-text-logo-clip">
          <rect width="168" height="20" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
};
